import React, { useState, useEffect, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import LaborPlanGrid from './LaborPlanGrid';
import LaborSummary from './LaborSummary';
import AvailableLaborChart from './AvailableLaborChart';
import RequiredLaborChart from './RequiredLaborChart';
import VolumeChart from './VolumeChart';
import WIPChart from './WIPChart';
import LaborOpportunities from './LaborOpportunities';
import CycleHeader from './CycleHeader';
import { buildAdherenceActuals } from './flowPlannerAdherenceModel';
import { TEXTS } from '../../utils/constants';
import { getCycleDisplayName } from '../../utils/helpers';

const Section = ({ children }) => <div className="fp-results-section">{children}</div>;

// Actuals key + show/hide toggle, shown only after the hidden unlock gesture.
// Additive only: when hidden the grid renders planned exactly as before. A failed
// or empty fetch shows a non-blocking warning.
const ActualsControlBar = ({ showActuals, onToggle, loading, refreshing, error, onDismissError }) => (
  <div className="fp-actuals-bar">
    <span className="fp-actuals-key">
      <span className="fp-plan-actual">Actual</span>
      <span className="fp-plan-actual-sep" aria-hidden="true">
        /
      </span>
      <span className="fp-actuals-key-planned">Planned</span>
    </span>
    <div style={{ flex: 1 }} />
    {loading && (
      <Text type={TEXTS.T3} color="secondary">
        Loading actuals…
      </Text>
    )}
    {!loading && refreshing && (
      <Row spacing="100" alignmentVertical="center">
        <Loader size="small" />
        <Text type={TEXTS.T3} color="secondary">
          Refreshing actuals…
        </Text>
      </Row>
    )}
    <button
      type="button"
      className="fp-actuals-toggle"
      aria-pressed={showActuals}
      onClick={onToggle}
      disabled={loading}>
      {showActuals ? 'Hide actuals' : 'Show actuals'}
    </button>
    {error && (
      <div className="fp-actuals-warning" role="status">
        {/* Meridian only renders the close (X) when size !== "small". */}
        <Alert type="warning" size="medium" onClose={onDismissError}>
          {error}
        </Alert>
      </div>
    )}
  </div>
);

const ResultsView = ({
  gridData,
  laborSummary,
  availableCycles,
  intervals,
  laborAvailabilityChart,
  requiredHCPerInterval,
  availableHCPerInterval,
  paidHoursPerInterval,
  opportunityHCPerInterval,
  volumeWIPChart,
  laborOpportunities,
  adherenceResponse = null,
  endedByInterval = [],
  actualsUnlocked = false,
  showActuals = false,
  adherenceLoading = false,
  adherenceRefreshing = false,
  adherenceError = null,
  onUnlockActuals,
  onToggleActuals,
  onDismissAdherenceError
}) => {
  const sortedCycles = [...availableCycles].sort();
  const [selectedCycle, setSelectedCycle] = useState(sortedCycles[0] || 'CYCLE_1');
  useEffect(() => {
    setSelectedCycle((prev) => (sortedCycles.length > 0 && !sortedCycles.includes(prev) ? sortedCycles[0] : prev));
  }, [availableCycles]);
  const gridCycleData = gridData?.[selectedCycle] || null;
  const currentTPH = Math.round(gridCycleData?.tph || 0);
  const volumeWIPCycleData = volumeWIPChart?.[selectedCycle] || null;

  // Recomputed per cycle since volume/rate are cycle-scoped.
  const adherenceActuals = useMemo(
    () => buildAdherenceActuals(adherenceResponse, intervals, selectedCycle),
    [adherenceResponse, intervals, selectedCycle]
  );
  if (!gridData) {
    return (
      <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
        <Column spacing="400" alignmentHorizontal="stretch" width="100%">
          <Box type="outline" spacingInset="400" style={{ backgroundColor: 'white', textAlign: 'center' }}>
            <Text type={TEXTS.T2} color="secondary">
              Run a plan to see results
            </Text>
          </Box>
        </Column>
      </Box>
    );
  }
  return (
    <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
      <div className="fp-results-panel">
        <Section>
          <CycleHeader
            cycles={sortedCycles}
            selectedCycle={selectedCycle}
            onSelect={setSelectedCycle}
            displayName={getCycleDisplayName}
            tph={currentTPH}
            onSecretUnlock={onUnlockActuals}
          />
          {actualsUnlocked && (
            <ActualsControlBar
              showActuals={showActuals}
              onToggle={onToggleActuals}
              loading={adherenceLoading}
              refreshing={adherenceRefreshing}
              error={adherenceError}
              onDismissError={onDismissAdherenceError}
            />
          )}
        </Section>
        <Section>
          <LaborPlanGrid
            gridCycleData={gridCycleData}
            intervals={intervals}
            actuals={adherenceActuals}
            showActuals={actualsUnlocked && showActuals}
            endedByInterval={actualsUnlocked && showActuals ? endedByInterval : []}
          />
        </Section>
        <Section>
          <LaborSummary laborSummary={laborSummary} intervals={intervals} />
        </Section>
        <Section>
          <AvailableLaborChart laborAvailabilityChart={laborAvailabilityChart} intervals={intervals} />
        </Section>
        <Section>
          <RequiredLaborChart intervals={intervals} gridCycleData={gridCycleData} />
        </Section>
        <Section>
          <VolumeChart volumeChartData={volumeWIPCycleData} intervals={intervals} />
        </Section>
        <Section>
          <WIPChart wipChartData={volumeWIPCycleData} intervals={intervals} />
        </Section>
        {Array.isArray(laborOpportunities) && laborOpportunities.length > 0 && (
          <Section>
            <LaborOpportunities
              requiredHCPerInterval={requiredHCPerInterval}
              availableHCPerInterval={availableHCPerInterval}
              opportunityHCPerInterval={opportunityHCPerInterval}
              requiredLaborHoursPerInterval={laborSummary?.grandTotals?.laborHours || []}
              paidHoursPerInterval={paidHoursPerInterval}
              laborOpportunities={laborOpportunities}
              intervals={intervals}
            />
          </Section>
        )}
      </div>
    </Box>
  );
};
export default ResultsView;
